import { isNativePlatform } from '@/renderer/utils/isNativePlatform';

/**
 * Store install badges for the native apps.
 *
 * Currently Android-only (the app is live on Google Play; iOS is still in
 * review). The list is data-driven so an App Store badge slots in later
 * without touching call sites — add an entry to `PLATFORMS` and it renders
 * everywhere this component is used.
 *
 * Hidden automatically inside the installed native app (Capacitor): there's
 * nothing to install once you're already in it.
 */

export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.raspicks.app';

type StorePlatform = {
  key: 'android' | 'ios';
  label: string;
  href: string;
  Badge: (props: { className?: string }) => JSX.Element;
};

/** Official four-color Google Play triangle mark. */
function PlayTriangle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 512 512" aria-hidden="true" className={className}>
      <path
        d="M48 40.6c-4.9 5.2-7.8 13.2-7.8 23.6v383.6c0 10.4 2.9 18.4 7.8 23.6l1.3 1.2 214.9-214.9v-5.1L49.3 39.4 48 40.6z"
        fill="#00d3ff"
      />
      <path
        d="M335.7 328.9l-71.6-71.6v-5.1l71.6-71.6 1.6.9 84.8 48.2c24.2 13.8 24.2 36.3 0 50.1l-84.8 48.2-1.6.9z"
        fill="#ffce00"
      />
      <path d="M337.3 328l-73.2-73.2L48 471c8 8.4 21.1 9.5 35.9 1.1l253.4-144.1z" fill="#ff3d47" />
      <path
        d="M337.3 181.6L83.9 37.5C69.1 29.1 56 30.2 48 38.6l216.1 216.2 73.2-73.2z"
        fill="#00f076"
      />
    </svg>
  );
}

/** Google Play badge — dark button matching the site chrome. */
function GooglePlayBadge({ className }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-3 rounded-lg bg-black px-4 py-2.5 ring-1 ring-white/25 transition group-hover:ring-white/50 ${className ?? ''}`}
    >
      <PlayTriangle className="h-6 w-6 shrink-0" />
      <span className="flex flex-col text-left text-white leading-none">
        <span className="font-medium text-[10px] text-zinc-300 uppercase tracking-wide">
          Get it on
        </span>{' '}
        <span className="mt-0.5 font-semibold text-lg leading-tight">Google Play</span>
      </span>
    </span>
  );
}

const PLATFORMS: StorePlatform[] = [
  {
    key: 'android',
    label: 'Get it on Google Play',
    href: GOOGLE_PLAY_URL,
    Badge: GooglePlayBadge,
  },
  // iOS (App Store) badge lands here once the app clears review.
];

export default function AppStoreBadges({ className }: { className?: string }) {
  // Nothing to install when already inside the native app.
  if (isNativePlatform()) {
    return null;
  }

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className ?? ''}`}>
      {PLATFORMS.map(({ key, href, Badge }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="group inline-flex rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
        >
          <Badge />
        </a>
      ))}
    </div>
  );
}
